<template>
  <v-row no-gutters class="fill-height">
    <v-col class="px-6 pt-2 pb-12 fill-height">
      <h1 class="display-1">
        {{ $t("devices") }}
        <span>
          {{ devicesLength }} {{ $t("total") }}
          <v-btn
          icon
          small
          color="textColor"
          class="refresh"
          @click="refreshData()"
          >
          <v-icon small>mdi-autorenew</v-icon>
        </v-btn>  
        </span>
      </h1>
      <h2 class="subtitle-2">{{ $t("deviceSubtitle") }}</h2>
      <InventoryFilter />
      <div class="pt-5 pb-5 d-flex align-end justify-space-between">
        <v-col class="pb-0" cols="5">
          <v-text-field v-model="model.search" append-icon="mdi-magnify" :label="searchLabel" single-line clearable
            hide-details />
        </v-col>
      </div>
      <v-sheet>
        <v-data-table :headers="tableheaders" :items="devices" :search="model.search" v-model="model.selected"
          fixed-header max-height="450px" item-key="tagId" class="installation-table max-height elevation-1"
          @click:row="selectDevice" :loading="isLoadingTable" :loading-text="$t('loadingData')">
          <template v-slot:[`item.type`]="{ item }">
            <v-icon v-if="item.type">
              {{getIconFromDeviceType(item.type)}}
            </v-icon>
          </template>
          <template v-slot:[`item.assigned_entity_name`]="{ item }">
            {{ item.assigned_entity_name ?? '-' }}
          </template>
          <template v-slot:[`item.entity_name`]="{ item }">
            {{ item.entity_name ?? '-' }}
          </template>
          <template v-slot:[`item.contact`]="{ item }">
            {{ item.contact ?? '-' }}
          </template>
          <template v-slot:[`item.hvac_model_id`]="{ item }">
            {{ `${item.hvac_brand} ${item.hvac_model}` }}
          </template>
          <template v-slot:[`item.onlineState`]="{ item }">
            <v-icon v-if="item.onlineState" color="success">
              mdi-wifi
            </v-icon>
            <v-icon v-else-if="!item.onlineState" color="error">
              mdi-wifi-alert
            </v-icon>
            <span v-else>
              -
            </span>
          </template>
          <template v-slot:[`item.fault`]="{ item }">
            <v-icon v-if="item.fault === 1" color="warning">
              mdi-alert-outline
            </v-icon>
            <v-icon v-else-if="item.fault === 0" color="success">
              mdi-heart
            </v-icon>
            <span v-else>
              -
            </span>
          </template>
          <template v-slot:[`item.client_name`]="{item}">
                <div v-if="item.installed">
                  {{ item.client_name }}
                </div>
                <div v-else>
                 <span>-</span>
                </div>
          </template>
          <template v-slot:[`item.make`]="{item}">
              <div>
                {{ item.make ?? '-' }}
              </div>
          </template>
          <template v-slot:[`item.model`]="{item}">
                <div>
                  {{ item.model ?? '-'}}
                </div>
          </template>
          <template v-slot:[`item.editor_access`]="{item}">
            <v-icon v-if="item.editor_access === true" color="success">
              mdi-check
            </v-icon>
            <v-icon v-else color="error">
              mdi-cancel
            </v-icon>
          </template>
          <template v-slot:no-data>
            {{ $t("devicesEmpty") }}
          </template>
          <template v-slot:[`item.id`]="{ item }">
            <Dropdown>
              <DropdownItem title="edit" @clicked="editDevice(item)" />
            </Dropdown>
          </template>
          <!-- does not use deviceInfo, but it needs to be attached to a value in devices, loggedIn is already used -->
        </v-data-table>
      </v-sheet>
    </v-col>
  </v-row>
</template>

<script lang="ts">
import { Component, Vue, Watch } from 'vue-property-decorator'
import { commonModule, installationsModule, distributorModule, devicesModule, filterModule } from '@/store/modules/store-accessor'
import Dropdown from '../components/dropdown/Dropdown.vue'
import InventoryFilter from '@/components/Filters/InventoryFilter.vue'
import DropdownItem from '../components/dropdown/DropdownItem.vue'
import FilterDropdown from '../components/dropdown/filter/FilterDropdown.vue'
import { VBreadcrumbsDivider } from 'vuetify/lib/components'
import {getIconFromDeviceType} from '@/helpers/data'
import KeycloakSession from '@/helpers/keycloak-session'

@Component({
  components: { Dropdown, DropdownItem, FilterDropdown, InventoryFilter }
})
export default class Inventory extends Vue {
  tableheaders: any [] = [
    { text: this.$t("type"), align: 'left', sortable: true, class: 'row-style', value: 'type' } ,
    {
      text: this.$t("serial_number"),
      align: 'left',
      class: 'row-style',
      sortable: true,
      value: 'serial_no'
    },
    {
      text: this.$t("device_id"),
      align: 'left',
      class: 'row-style',
      sortable: true,
      value: 'device_id'
    },
    { text: this.$t("assigned_company_name"), sortable: true, class: 'row-style', value: 'assigned_entity_name' },
    { text: this.$t("notes"), sortable: true, class: 'row-style', value: 'notes' },
    { text: this.$t("installer_company_name"), sortable: true, class: 'row-style', value: 'entity_name' },
    { text: this.$t("client_name"), sortable: true, class: 'row-style', value: 'client_name' },
    { text: this.$t("hvac_brand"), sortable: true, class: 'row-style', value: 'hvac_model_id' },
    { text: this.$t("online"), sortable: true, class: 'row-style', value: 'onlineState' },
    { text: this.$t("boiler_status"), sortable: true, class: 'row-style', value: 'fault' },
    { text: this.$t("editor_access"), sortable: true, class: 'row-style', value: 'editor_access' },
    { text: '', align: 'right', sortable: false, class: 'row-style', value: 'id' } 
  ]

  model: any = {
    search: '',
    selected: [] as string[]
  }

  get devicesLength() {
    return distributorModule.inventoryCount
  }

  get kcSession() {
    return new KeycloakSession()
  }

  get isNotAdmin() {
      return this.kcSession.userIsNotAdmin();
  }

  get searchLabel() {
    return this.$t("devicesSearch")
  }

  get devices(){
    return distributorModule.inventory
  }

  get currInstallation(){
    return installationsModule.currentInstallation
  }

  get isLoadingTable() {
    return distributorModule.isLoadingTable;
  }

  getIconFromDeviceType(deviceType: string) {
    return getIconFromDeviceType(deviceType);
  }

  async refreshData() {
    distributorModule.getInventoryCount();
    distributorModule.getDevices(distributorModule.filters);
    filterModule.getInventoryFilters();
  }

  async mounted () {
    /*
     TODO: Handle installations properly when UX is clear.
     */
    distributorModule.getInventoryCount();
    distributorModule.getDevices(distributorModule.filters);
    filterModule.getInventoryFilters();
    commonModule.initSnackbar({});

    if(this.isNotAdmin) {
      this.tableheaders = this.tableheaders.filter(header => header.value !== 'editor_access');
    }

  }

  async selectDevice (device: any) {
    // if(device.installation_id) {
      distributorModule.setIsLoadingTable(true);
      await devicesModule.getDeviceInfo(device.device_id);
      distributorModule.setIsLoadingTable(false);
      commonModule.showSideview({ name: 'distributor-device-sideview' , payload: { deviceId: device.device_id, tab: 0 } })
    //}
  }

  async editDevice (device: any) {
    commonModule.showModal({ name: 'edit-inventory-device-modal', payload: { device: device} })
  }
}
</script>

<style scoped lang="scss">

.installation-table:not(thead) {
  cursor: pointer;
  min-width: 600px;
  border: 1px solid rgba(0, 0, 0, 0.12);
}


.max-height {
  max-height: 65vh;
  overflow: auto;
}

.placeholder {
  background-color: grey
}

h1 {
  margin-top: 40px;
  font-size: 35px;
  font-weight: 500;
  line-height: 65px;
}

span {
  margin-left: 10px;
  font-size: 18px;
  font-weight: 400;
  color: $pale-purple;
}
</style>
